import * as React from "react";
import { withRouter } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import * as cn from "classnames/bind";
import CoverControlsAccordion from "layout/common/controls-area/cover-controls-accordion";
import CalendarControlsAccordion from "layout/common/controls-area/calendar-controls-accordion";
import SummaryControlsAccordion from "../summary-controls-accordion";
import * as styles from "./controls-area.css";
import ControlsNavigation from "../controls-navigation";
import {
    NavigationComponent,
    NavigationComponents,
    DefaultSize,
    CalendarSteps,
    SvgCalendarLayout,
    CalendarStepGuide,
    FastTrackStepGuide,
    NotebookStepGuide,
    DefaultCulture,
} from "ui/constants";
import { actionCreators as uiActionCreators } from "ui/store";
import { ExtraPagesControlsAccordion } from "../extra-pages/";
import { MonthDividerControlsAccordion } from "../month-divider/";
import { getNavigationComponentsFromStepGuide } from "ui/helper";
import { CalendarType } from "svg/constants";
import { getComponentRoute } from "page/calendar/helper";
import {
    CalendarState,
    CustomImage,
    ImageDetails,
    RubberBandItem,
    SpiralItem,
} from "burde";

const cx = cn.bind(styles);

const controlsAreaCss = () =>
    cx({
        controls: true,
    });

const controlsInnerCn = () =>
    cx({
        controlsInner: true,
    });

type ControlsAreaProps = {
    activeComponent: NavigationComponent;
    calendar: CalendarState;
    fastTrackStatus: boolean;
    calendarLayout: SvgCalendarLayout | null;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
    customCoverImage: CustomImage | null;
    customBackImage: CustomImage | null;
    spiralItem: SpiralItem | null;
    rubberbandItem: RubberBandItem | null;
    calendarType: string;
} & typeof uiActionCreators &
    RouteComponentProps;

const ControlsArea: React.FC<ControlsAreaProps> = (props) => {
    const {
        activeComponent,
        calendar,
        fastTrackStatus,
        calendarLayout,
        coverImage,
        backCoverImage,
        customCoverImage,
        customBackImage,
        spiralItem,
        rubberbandItem,
    } = props;

    let sizeName =
        calendar.calendarSize != undefined
            ? calendar.calendarSize
            : DefaultSize;

    const layoutHasWeekModules = () => {
        return calendarLayout !== null && calendarLayout.maxWeekModules > 0;
    };

    const layoutHasDayModules = () => {
        return calendarLayout !== null && calendarLayout.maxDayModules > 0;
    };

    const getNumberOfSteps = () => {
        const components = getCurrentNavigationComponents(
            NavigationComponents,
            fastTrackStatus,
            layoutHasDayModules(),
            layoutHasWeekModules(),
            calendar.calendarType === CalendarType.Notebook,
        );

        if (components === false) {
            return 1;
        }

        return components.length;
    };

    const getCurrentStep = () => {
        const components = getCurrentNavigationComponents(
            NavigationComponents,
            fastTrackStatus,
            layoutHasDayModules(),
            layoutHasWeekModules(),
            calendar.calendarType === CalendarType.Notebook,
        );

        if (components === false) {
            return 1;
        }

        const currentStep = NavigationComponents
            ? components.indexOf(
                  components.find(
                      (x) => x.name === activeComponent.name,
                  ) as NavigationComponent,
              ) + 1
            : undefined;

        return currentStep !== undefined ? currentStep : 1;
    };

    const navigationCounter = () => {
        const currentStep = getCurrentStep();
        const numberOfSteps = getNumberOfSteps();
        return `${currentStep} / ${numberOfSteps}`;
    };

    const getCurrentNavigationComponents = (
        navigationComponents: NavigationComponent[],
        isFastTrack: boolean,
        layoutHasDayModules: boolean,
        layoutHasWeekModules: boolean,
        isNotebook: boolean,
    ) => {
        let calendarSteps = CalendarStepGuide;

        if (isNotebook) {
            calendarSteps = NotebookStepGuide;
        } else if (isFastTrack) {
            calendarSteps = FastTrackStepGuide;
        }

        const currentSteps = calendarSteps.find(
            (x) => x.type == sizeName || x.type == "A5",
        );

        if (currentSteps === undefined) {
            return false;
        }

        const newSteps = { ...currentSteps };

        if (!layoutHasDayModules) {
            newSteps.steps = newSteps.steps.filter(
                (step) => step !== "dayModules",
            );
        }

        if (!layoutHasWeekModules) {
            newSteps.steps = newSteps.steps.filter(
                (step) => step !== "weekModules",
            );
        }

        return getNavigationComponentsFromStepGuide(
            newSteps,
            navigationComponents,
        );
    };

    const toggleNavigationComponent = (type: string) => {
        const components = getCurrentNavigationComponents(
            NavigationComponents.slice(0),
            fastTrackStatus,
            layoutHasDayModules(),
            layoutHasWeekModules(),
            calendar.calendarType === CalendarType.Notebook,
        );

        if (components === false) {
            return false;
        }

        const currentComponent = components.find(
            (x) => x.name === activeComponent.name,
        );

        if (currentComponent === undefined) {
            return components[0];
        }

        const currentIndex = components.indexOf(currentComponent);
        const newIndex = type === "prev" ? currentIndex - 1 : currentIndex + 1;

        return components[newIndex];
    };

    const changeActiveSection = (type: string) => {
        let targetComponent = activeComponent;

        const nextComponent = toggleNavigationComponent(type);

        if (nextComponent !== false && nextComponent !== undefined) {
            targetComponent = nextComponent;
        }

        props.UpdateActiveNavigationComponent(targetComponent);

        const targetComponentRoute = getComponentRoute(
            targetComponent.route,
            calendar.calendarSize,
            calendar.calendarType,
        );

        if (props.location.pathname !== targetComponentRoute) {
            props.history.push(targetComponentRoute);
        }

        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <>
            {activeComponent.step === CalendarSteps.extraPages ? (
                <div className={controlsAreaCss()}>
                    <ExtraPagesControlsAccordion>
                        <ControlsNavigation
                            CurrentText={props.activeComponent.resourceKey}
                            CounterText={navigationCounter()}
                            clickPrev={() => {
                                changeActiveSection("prev");
                            }}
                            clickNext={() => {
                                changeActiveSection("next");
                            }}
                        />
                    </ExtraPagesControlsAccordion>
                </div>
            ) : (
                <div className={controlsAreaCss()}>
                    <div className={controlsInnerCn()}>
                        {activeComponent.step == CalendarSteps.cover ? (
                            <CoverControlsAccordion
                                coverImage={coverImage}
                                backCoverImage={backCoverImage}
                            />
                        ) : activeComponent.step == CalendarSteps.ownerPage ? (
                            <CalendarControlsAccordion />
                        ) : activeComponent.step == CalendarSteps.insert ? (
                            <CalendarControlsAccordion />
                        ) : activeComponent.step == CalendarSteps.summary ? (
                            <SummaryControlsAccordion />
                        ) : activeComponent.step ==
                          CalendarSteps.monthDivider ? (
                            <MonthDividerControlsAccordion />
                        ) : (
                            <CoverControlsAccordion
                                coverImage={coverImage}
                                backCoverImage={backCoverImage}
                            />
                        )}
                    </div>
                    <ControlsNavigation
                        CurrentText={
                            calendar.calendarType === CalendarType.Notebook &&
                            props.activeComponent.name === "weekModules"
                                ? "Notebook_WeekModules"
                                : props.activeComponent.resourceKey
                        }
                        CounterText={navigationCounter()}
                        clickPrev={() => {
                            changeActiveSection("prev");
                        }}
                        clickNext={() => {
                            changeActiveSection("next");
                        }}
                    />
                </div>
            )}
        </>
    );
};

export default ControlsArea;
